import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { User } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const ACC_BG = "#E8E0D5";
const ACC_INK = "#2C2820";
const ACC_SUMMARY = "#6B6560";
const ACC_CORAL = "#C84B2F";
const ACC_CARD = "#FFFCF8";
const ACC_BORDER = "#E8E2D9";

function pluralize(n: number, singular: string, plural?: string): string {
  return `${n} ${n === 1 ? singular : (plural ?? `${singular}s`)}`;
}

interface GuestAccountProps {
  handle: string | null;
  createdAt: string | null;
  outfitCount: number;
  savedCount: number;
  onSignup: () => void;
}

export function GuestAccount({
  handle,
  createdAt,
  outfitCount,
  savedCount,
  onSignup,
}: GuestAccountProps) {
  const navigate = useNavigate();

  const daysSinceFirstVisit = createdAt
    ? Math.max(
        0,
        Math.floor(
          (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24),
        ),
      )
    : 0;

  const isFresh = outfitCount === 0 && daysSinceFirstVisit < 1;

  useEffect(() => {
    trackEvent("guest_account_viewed", {
      handle,
      days_since_first_visit: daysSinceFirstVisit,
      outfit_count: outfitCount,
      saved_count: savedCount,
    });
    // mount-only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statsLine = isFresh
    ? "Just getting started"
    : [
        pluralize(outfitCount, "outfit analysed", "outfits analysed"),
        pluralize(savedCount, "saved"),
        daysSinceFirstVisit === 0
          ? "new today"
          : `${pluralize(daysSinceFirstVisit, "day")} using Like2Fit`,
      ].join(" · ");

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        background: ACC_BG,
        minHeight: "100dvh",
        paddingTop: "var(--safe-top)",
        paddingBottom: "calc(var(--safe-bottom) + 100px)",
        paddingLeft: 14,
        paddingRight: 14,
      }}
    >
      <div style={{ marginBottom: 16, padding: "16px 4px 0" }}>
        <p
          style={{
            fontSize: 13,
            textTransform: "uppercase",
            color: ACC_CORAL,
            letterSpacing: "0.08em",
            fontWeight: 500,
            margin: 0,
          }}
        >
          GUEST
        </p>
        <h1
          style={{
            fontFamily: "var(--font-serif, var(--font-display))",
            fontStyle: "italic",
            fontSize: 24,
            lineHeight: 1.1,
            color: ACC_INK,
            marginTop: 4,
            marginBottom: 4,
            minHeight: 28,
          }}
        >
          {handle ?? "\u00A0"}
        </h1>
        <p
          style={{
            fontSize: 13,
            color: ACC_SUMMARY,
            margin: 0,
            marginTop: 2,
          }}
        >
          {statsLine}
        </p>
      </div>

      <div
        style={{
          background: ACC_CARD,
          border: `0.5px solid ${ACC_BORDER}`,
          borderRadius: 20,
          padding: "32px 20px",
          textAlign: "center",
          marginTop: 24,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#FAECE7",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <User size={24} style={{ color: ACC_CORAL }} />
        </div>
        <p
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            color: ACC_CORAL,
            letterSpacing: "0.07em",
            fontWeight: 500,
            marginTop: 12,
            margin: 0,
          }}
        >
          MAKE LIKE2FIT YOURS
        </p>
        <h2
          style={{
            fontFamily: "var(--font-serif, var(--font-display))",
            fontStyle: "italic",
            fontSize: 19,
            color: ACC_INK,
            marginTop: 6,
            margin: 0,
          }}
        >
          Sign up to keep your looks
        </h2>
        <p
          style={{
            fontSize: 13,
            color: ACC_SUMMARY,
            lineHeight: 1.5,
            marginTop: 8,
            margin: 0,
          }}
        >
          Save outfits permanently, add your wardrobe, and unlock Pairs with + Fills a gap.
        </p>
        <button
          onClick={onSignup}
          style={{
            background: ACC_CORAL,
            color: "#fff",
            borderRadius: 22,
            padding: "10px 22px",
            fontSize: 13,
            fontWeight: 500,
            width: "100%",
            marginTop: 16,
            border: "none",
            cursor: "pointer",
          }}
        >
          Sign up free
        </button>
      </div>

      <div style={{ marginTop: 8 }}>
        <button
          onClick={() => navigate({ to: "/about" })}
          style={{
            width: "100%",
            background: ACC_CARD,
            border: `0.5px solid ${ACC_BORDER}`,
            borderRadius: 12,
            padding: "14px 16px",
            textAlign: "left",
            fontSize: 14,
            color: ACC_INK,
            marginBottom: 8,
            cursor: "pointer",
          }}
        >
          About Like2Fit
        </button>
        <button
          onClick={() => navigate({ to: "/privacy" })}
          style={{
            width: "100%",
            background: ACC_CARD,
            border: `0.5px solid ${ACC_BORDER}`,
            borderRadius: 12,
            padding: "14px 16px",
            textAlign: "left",
            fontSize: 14,
            color: ACC_INK,
            marginBottom: 8,
            cursor: "pointer",
          }}
        >
          Privacy policy
        </button>
        <button
          onClick={() => navigate({ to: "/terms" })}
          style={{
            width: "100%",
            background: ACC_CARD,
            border: `0.5px solid ${ACC_BORDER}`,
            borderRadius: 12,
            padding: "14px 16px",
            textAlign: "left",
            fontSize: 14,
            color: ACC_INK,
            cursor: "pointer",
          }}
        >
          Terms of service
        </button>
      </div>
    </div>
  );
}
